import React, { forwardRef, MouseEventHandler } from "react";
import { WithTesting } from "../../types";
import {
  LabeledIconButtonStyled,
  Label,
  LabelIconButtonWrapper,
  IconStyled,
} from "./LabeledIconButton.styled";
import Tooltip, { TooltipProps } from "../Tooltip";

export interface LabeledIconButtonProps {
  /**
   * The name of the icon.
   */
  name: string;

  /**
   * Button text (below icon).
   */
  label: string;

  /**
   * Disabled button state.
   */
  disabled?: boolean;

  /**
   * Show button in selected state.
   */
  isSelected?: boolean;

  /**
   * Click handler
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;

  /**
   * Wrap mode is used to tell how BigButton should work with long label, if the mode is `wrap` -
   * it wraps text on a new line, if it's `ellipsis` - adds three dots in the end.
   */
  wrapMode?: "wrap" | "ellipsis";

  /**
   * Tooltip props
   */
  tooltipProps: Omit<TooltipProps, "children">;
}

const LabeledIconButton = forwardRef<
  HTMLButtonElement,
  LabeledIconButtonProps & WithTesting
>(
  (
    {
      label,
      name,
      dataTestId,
      disabled,
      onClick,
      wrapMode = "ellipsis",
      tooltipProps,
      isSelected,
    },
    ref,
  ) => {
    return (
      <Tooltip {...tooltipProps}>
        <LabelIconButtonWrapper>
          <LabeledIconButtonStyled
            $isSelected={isSelected}
            disabled={disabled}
            onClick={onClick}
            ref={ref}
            data-testid={dataTestId}
          >
            <IconStyled name={name} size="medium-big" />
            <Label wrapMode={wrapMode}>{label}</Label>
          </LabeledIconButtonStyled>
        </LabelIconButtonWrapper>
      </Tooltip>
    );
  },
);

export default LabeledIconButton;
